import { QuacInfo } from "../api/types";
import { Logger } from "./logger";

export function hasValidQuacParams({
  clientId,
  signUpFirstName,
  signUpLastName,
  signUpEmail,
}: QuacInfo): { isValid: boolean; message?: string } {
  const hasAllFields = Boolean(
    clientId && signUpFirstName && signUpLastName && signUpEmail,
  );

  if (!hasAllFields) {
    return {
      isValid: false,
      message:
        "client-id, sign-up-first-name, sign-up-last-name, sign-up-email are" +
        " all required if you intend to use Quick User Account Creation",
    };
  }

  // If default code snippet values are used don't use quac
  // https://gitlab.com/zapier/partner/-/blob/staging/src/pages/solutions/full-experience.tsx?ref_type=heads#L271-284
  const hasDefaultCodeSnippets =
    signUpEmail?.endsWith("@example.com") ||
    signUpFirstName === "first_name_of_your_user" ||
    signUpLastName === "last_name_of_your_user";

  if (hasDefaultCodeSnippets) {
    return {
      isValid: false,
      message:
        "Default code snippet values provided. Update sign-up-first-name, sign-up-last-name, sign-up-email if you intend to use Quick User Account Creation",
    };
  }

  return { isValid: true };
}

export function urlWithQuACParams(
  url: string,
  {
    clientId = "",
    signUpFirstName = "",
    signUpLastName = "",
    signUpEmail = "",
  }: QuacInfo = {},
): string {
  const { isValid, message } = hasValidQuacParams({
    clientId,
    signUpFirstName,
    signUpLastName,
    signUpEmail,
  });

  // If we don't have valid QuAC params, return the original URL
  if (!isValid) {
    // In most cases, the developer simply didn't _try_ to use QuAC, so we don't
    // want to spam them with error messages. Only share the error message if
    // they seem to have tried to use QuAC to begin with.
    if (signUpFirstName || signUpLastName || signUpEmail) {
      Logger.info(message);
    }
    return url;
  }

  const quacURL = new URL(url);
  quacURL.searchParams.set("sign-up-first-name", signUpFirstName);
  quacURL.searchParams.set("sign-up-last-name", signUpLastName);
  quacURL.searchParams.set("sign-up-email", signUpEmail);
  // QuAC uses client-id in place of provider
  quacURL.searchParams.delete("provider");
  quacURL.searchParams.set("client-id", clientId);

  return quacURL.href;
}
